import type { KoreanSeoLocale } from "./locale-rollout";
import type { Wave1HomeContent } from "./wave1-home";

const useCases = {
  youtube: "/faceless-youtube-video-generator",
  tiktok: "/faceless-tiktok-video-generator",
  instagram: "/faceless-instagram-reels-generator",
  podcast: "/faceless-podcast-video-generator",
};

export const koreanHomeContent: Record<
  KoreanSeoLocale,
  Wave1HomeContent
> = {
  ko: {
    metadata: {
      title: "AI 얼굴 없는 영상 생성기 및 자동 게시 | AriaFlow",
      description:
        "아이디어, 대본 또는 공개 URL에서 편집 가능한 얼굴 없는 영상을 만드세요. AI 음성, 원본 이미지, 자막, 검토와 소셜 게시를 지원합니다.",
      keywords: [
        "AI 얼굴 없는 영상 생성기",
        "얼굴 없는 유튜브 채널",
        "AI 얼굴 없는 영상 만들기",
        "페이스리스 영상 제작",
      ],
    },
    hero: {
      eyebrow: "AI 얼굴 없는 영상 생성기",
      title: "얼굴 없는 영상을 자동으로 만들고 게시하세요",
      description:
        "아이디어, 대본 또는 공개 URL로 시작하세요. 모든 세부 사항을 검토한 뒤 일정에 맞춰 게시하거나 준비된 워크플로를 자동화할 수 있습니다.",
      primary: "무료로 시작하기",
      secondary: "실제 샘플 보기",
      previewAlt:
        "콘텐츠 계획과 생성 상태를 편집할 수 있는 AriaFlow 얼굴 없는 영상 워크플로",
      caption:
        "게시 전에 검토하거나 준비된 워크플로를 자동화할 수 있습니다.",
    },
    workflowTypes: {
      title: "보유한 자료에서 바로 시작하세요.",
      subtitle:
        "주제, 완성된 대본 또는 공개 URL 중 하나를 입력하고, 생성이나 게시 전에 초안을 검토하세요.",
      action: "워크플로 살펴보기",
      items: [
        {
          title: "주제로 영상 만들기",
          description:
            "구체적인 아이디어에서 콘텐츠 계획과 첫 번째 대본을 만듭니다.",
          output: "새 시리즈와 반복 주제에 적합",
          href: "/faceless-video-automation",
        },
        {
          title: "대본으로 영상 만들기",
          description:
            "승인된 내레이션을 유지하면서 텍스트를 편집 가능한 장면으로 나눕니다.",
          output: "작가와 검토가 끝난 콘텐츠에 적합",
          href: "/script-to-faceless-video",
        },
        {
          title: "URL로 영상 만들기",
          description:
            "공개 자료를 바탕으로 내레이션과 시각 초안을 구성합니다.",
          output: "기사와 설명형 콘텐츠에 적합",
          href: "/url-to-faceless-video",
        },
      ],
    },
    samples: {
      title: "AriaFlow로 만든 실제 9:16 세로 영상",
      description:
        "스토리, 영어 대화, 도서 리뷰, 긴 에피소드까지 모두 실제 AriaFlow 워크플로에서 만든 샘플입니다. 직접 만들기 전에 구성, 자막, 속도감과 비주얼 방향을 비교해 보세요.",
      cta: "이 형식으로 영상 만들기",
      publicExample: "공개 샘플 보기",
      englishDisclosure: "현재 샘플은 영어 음성 또는 영어 자막을 사용합니다.",
      secondShort: "초",
      minuteShort: "분",
      items: {
        history: {
          label: "역사",
          title: "간결한 역사 이야기",
          description:
            "내레이션, 시대별 이미지와 자막으로 구성한 세로형 역사 영상입니다.",
          transcript:
            "짧은 도입으로 질문을 제시하고, 핵심 사건을 순서대로 설명한 뒤 맥락을 요약합니다.",
        },
        scary: {
          label: "공포 이야기",
          title: "긴장감 있는 짧은 이야기",
          description:
            "장면별 이미지와 속도감 있는 내레이션을 결합한 세로형 스토리 영상입니다.",
          transcript:
            "배경을 빠르게 소개하고 단서를 단계적으로 공개한 뒤 마지막 장면에서 반전을 전달합니다.",
        },
        longForm: {
          label: "장편",
          title: "장편 설명형 영상",
          description:
            "여러 장면, 음성, 자막을 하나의 긴 흐름으로 구성한 가로형 영상입니다.",
          transcript:
            "주제를 여러 구간으로 나누고 각 구간마다 근거, 예시와 시각 자료를 배치합니다.",
        },
        education: {
          label: "URL을 영상으로",
          title: "AriaFlow 홈페이지 URL 영상 예시",
          description:
            "AriaFlow 공개 홈페이지를 출처 기반 내레이션, 자막, 제품 장면이 포함된 79초 가로형 영상으로 변환한 예시입니다.",
          transcript:
            "공개 페이지를 바탕으로 아이디어, 스크립트 또는 URL에서 편집 가능한 영상을 만들고 연결된 채널에 게시하는 과정을 소개합니다.",
        },
        url: {
          label: "URL 변환",
          title: "공개 자료를 바탕으로 만든 영상",
          description:
            "공개 URL에서 핵심 내용을 추려 새로운 내레이션과 장면으로 재구성한 영상입니다.",
          transcript:
            "자료의 핵심 주장을 확인하고 말하기에 적합한 구조로 다시 쓴 뒤 출처를 검토합니다.",
        },
      },
    },
    process: {
      title: "입력부터 검토된 영상까지",
      description:
        "자동화는 반복 작업을 줄이지만, 생성과 게시 사이에는 항상 검토 단계가 있습니다.",
      steps: [
        {
          title: "1. 입력 선택",
          description: "주제, 대본 또는 공개 URL과 영상 형식을 지정합니다.",
        },
        {
          title: "2. 영상 생성",
          description:
            "AriaFlow가 대본, 장면, AI 음성, 자막과 영상을 준비합니다.",
        },
        {
          title: "3. 결과 검토",
          description:
            "사실, 발음, 이미지, 자막과 게시 대상을 확인하고 필요한 부분을 수정합니다.",
        },
        {
          title: "4. 다운로드 또는 게시",
          description:
            "승인한 영상을 다운로드하거나 연결된 소셜 계정에 예약·게시합니다.",
        },
      ],
      controlsTitle: "게시 전에 직접 제어할 수 있는 항목",
      controls: [
        "대본과 장면 순서",
        "이미지 스타일과 화면 비율",
        "AI 음성, 발음과 배경 음악",
        "자막과 게시 대상",
      ],
      platformsEyebrow: "소셜 게시",
      platformsTitle: "한 곳에서 네 개 플랫폼으로",
      platformsDescription:
        "지원되는 계정을 연결하면 검토가 끝난 영상을 YouTube, TikTok, Instagram과 X에 게시할 수 있습니다.",
      platformsSupportedLabel: "지원 플랫폼",
      platformsDisclaimer:
        "게시 기능은 플랫폼 계정 유형, 권한, API 제한과 각 플랫폼 정책에 따라 달라질 수 있습니다.",
      directoryTitle: "워크플로별 기능 살펴보기",
      directoryDescription:
        "입력 방식과 게시 대상에 맞는 얼굴 없는 영상 제작 경로를 선택하세요.",
      directoryLabel: "전체 사용 사례",
    },
    pricing: {
      title: "영상 제작량에 맞는 플랜",
      subtitle:
        "크레딧을 예상 영상 수와 분량으로 비교하세요. 실제 소모량은 모델과 장면 구성에 따라 달라집니다.",
      view: "전체 가격 보기",
      credits: "월 크레딧",
      estimate: "예상 제작량",
      disclaimer:
        "표시된 영상 수와 분량은 추정치입니다. 선택한 모델, 영상 길이, 장면 수와 재생성 횟수에 따라 달라집니다.",
      perYear: "/년",
      perMonth: "/월",
      minutes: "영상 분",
      planDescriptions: {
        basic: "첫 번째 얼굴 없는 영상 워크플로를 시험하는 개인 크리에이터용",
        standard: "정기적으로 여러 영상을 제작하는 크리에이터용",
        pro: "여러 시리즈를 운영하는 팀과 대량 제작용",
      },
    },
    faq: {
      title: "AI 얼굴 없는 영상 생성기 FAQ",
      description:
        "제작, 편집, 영상 길이와 소셜 게시에 관한 주요 질문입니다.",
      items: [
        {
          question: "얼굴 없는 영상이란 무엇인가요?",
          answer:
            "진행자가 카메라에 직접 등장하지 않고 내레이션, 이미지, 영상, 자막과 음악으로 내용을 전달하는 영상입니다.",
        },
        {
          question: "카메라나 직접 녹음한 음성이 필요한가요?",
          answer:
            "필수는 아닙니다. 대본, AI 이미지와 AI 음성으로 만들 수 있으며, 원하면 직접 만든 자료를 추가할 수 있습니다.",
        },
        {
          question: "어떤 입력을 사용할 수 있나요?",
          answer:
            "주제, 완성된 대본 또는 읽을 수 있는 공개 URL에서 시작할 수 있습니다.",
        },
        {
          question: "최대 영상 길이는 얼마인가요?",
          answer:
            "현재 얼굴 없는 영상 워크플로는 최대 10분 영상을 지원합니다. 긴 대본은 구간을 나누거나 압축해야 할 수 있습니다.",
        },
        {
          question: "생성 후에 수정할 수 있나요?",
          answer:
            "네. 다운로드나 게시 전에 대본, 장면, 이미지, 음성, 자막과 음악을 검토하고 수정할 수 있습니다.",
        },
        {
          question: "어디에 게시할 수 있나요?",
          answer:
            "지원되는 계정과 권한을 연결하면 YouTube, TikTok, Instagram과 X에 게시할 수 있습니다. 최종 지원 범위는 플랫폼 정책에 따라 달라집니다.",
        },
      ],
    },
    cta: {
      title: "검토할 수 있는 얼굴 없는 영상을 만들어 보세요.",
      description:
        "아이디어, 대본 또는 URL로 시작하고 다운로드나 게시 전에 전체 결과를 확인하세요.",
      button: "얼굴 없는 영상 만들기",
    },
    navigation: {
      facelessVideo: "얼굴 없는 영상",
      useCases: "사용 사례",
      resources: "자료",
      pricing: "가격",
      dashboard: "대시보드",
      signIn: "로그인",
      startFree: "무료로 시작",
      menu: "메뉴",
      close: "닫기",
      useCaseLinks: [
        {
          href: useCases.youtube,
          label: "YouTube 영상",
          description: "Shorts와 장편 채널 영상을 만듭니다.",
        },
        {
          href: useCases.tiktok,
          label: "TikTok 영상",
          description: "읽기 쉬운 자막이 있는 세로형 영상을 만듭니다.",
        },
        {
          href: useCases.instagram,
          label: "Instagram 릴스",
          description: "얼굴을 공개하지 않고 일관된 릴스를 만듭니다.",
        },
        {
          href: useCases.podcast,
          label: "팟캐스트 영상",
          description: "대화형 대본을 시각적인 에피소드로 바꿉니다.",
        },
      ],
    },
    footer: {
      description:
        "아이디어, 대본 또는 URL에서 편집 가능한 얼굴 없는 영상을 만들고 다운로드나 게시 전에 검토하세요.",
      productTitle: "얼굴 없는 영상",
      useCasesTitle: "사용 사례",
      resourcesTitle: "자료",
      terms: "이용약관",
      privacy: "개인정보 처리방침",
      english: "영어",
      productLinks: [
        ["/script-to-faceless-video", "대본으로 영상 만들기"],
        ["/url-to-faceless-video", "URL로 영상 만들기"],
        ["/long-form-faceless-video-generator", "장편 영상"],
        ["/faceless-video-automation", "영상 자동화"],
      ],
      useCaseLinks: [
        [useCases.youtube, "YouTube 영상"],
        [useCases.tiktok, "TikTok 영상"],
        [useCases.instagram, "Instagram 릴스"],
        [useCases.podcast, "팟캐스트 영상"],
      ],
      resourceLinks: [
        ["/blog", "Faceless Video 블로그 (영어)"],
        [
          "/resources/faceless-video-script-templates",
          "영상 대본 템플릿 (영어)",
        ],
        [
          "/tools/faceless-video-length-calculator",
          "영상 길이 계산기 (영어)",
        ],
        ["/pricing", "가격"],
      ],
    },
    marketing: {
      create: "얼굴 없는 영상 만들기",
      watchSample: "샘플 보기",
      englishSample: "이 샘플의 음성 또는 자막은 영어입니다.",
      whoFits: "이 워크플로가 적합한 대상",
      workflowTitle: "입력부터 승인된 영상까지",
      limitsTitle: "시작 전에 확인할 제한 사항",
      relatedTitle: "관련 가이드와 워크플로",
      faqTitle: "자주 묻는 질문",
      finalTitle: "검토할 수 있는 얼굴 없는 영상 만들기",
      finalDescription:
        "아이디어, 대본 또는 URL로 시작하고 다운로드, 예약 또는 게시 전에 결과를 검토하세요.",
      englishLabel: "영어",
      viewPublicExample: "공개 샘플 보기",
    },
  },
};
